<script lang="ts" generics="TValues">
    import type { Field, Form } from '@jmvdevelop/svelte-form';
    import { createSelect, createSync, melt } from '@melt-ui/svelte';
    import { fly } from 'svelte/transition';
    import { cx } from '$lib/utils/cx';
    import { createResizeObserver } from '$lib/utils/resizeObserver';
    import { match } from 'ts-pattern';
    import type { SearchHeaderFormData_TypeEnum } from '$gql/graphql';

    // Use types from search header type instead ?
    export let form: Form<TValues, string>;
    export let field: Field<TValues, SearchHeaderFormData_TypeEnum>;

    // rounded-icon variant is for the desktop search header, but reduced form.
    export let variant: 'full' | 'just-icon' | 'rounded-icon' = 'full';

    const { useResizeObserver: resizeObserver, entries } = createResizeObserver();
    let height = 42;
    $: height = $entries[0]?.borderBoxSize[0]?.blockSize ?? 42;

    //@TODO icon for job
    let choices = [
        { value: 'company' as const, label: 'Sociétés', icon: 'tnp tnp-company' },
        { value: 'pro' as const, label: 'Professionnels', icon: 'tnp tnp-user' },
        { value: 'newsfeed' as const, label: 'Réseaux', icon: 'tnp tnp-network' },
        { value: 'job' as const, label: 'Emplois', icon: 'tnp tnp-briefcase' },
    ];

    function onBlur() {
        form.dispatch(form.actions().blur(field));
    }

    function onFocus() {
        form.dispatch(form.actions().focus(field));
    }

    const {
        elements: { trigger, menu, option },
        states: { open, selected },
        options: { positioning: _positioning },
    } = createSelect<SearchHeaderFormData_TypeEnum>({
        forceVisible: true,
        positioning: match(variant)
            .with(
                'just-icon',
                () =>
                    ({
                        placement: 'bottom-start',
                        sameWidth: false,
                        gutter: -height,
                    }) as const,
            )
            .with(
                'rounded-icon',
                () =>
                    ({
                        placement: 'bottom-start',
                        sameWidth: false,
                        gutter: -height,
                    }) as const,
            )
            .with(
                'full',
                () =>
                    ({
                        placement: 'bottom',
                        sameWidth: true,
                        gutter: -1,
                    }) as const,
            )
            .exhaustive(),
        onSelectedChange: ({ next }) => {
            if (next != null) {
                form.dispatch(form.actions().changeValue(next.value, field));
            }

            return next;
        },
    });

    $: $_positioning = {
        ...$_positioning,
        ...(variant !== 'full' ? { gutter: -height } : {}),
    };

    $: v = form.stores().fieldValue(field);
    $: choice = choices.find((choice) => choice.value === $v);

    const sync = createSync({ selected });
    $: sync.selected(choice, (selected) => {
        form.dispatch(form.actions().changeValue(selected?.value ?? 'company', field));
    });
</script>

{#if variant === 'full'}
    <div class="relative">
        <button
            use:melt={$trigger}
            on:focus={onFocus}
            on:blur={onBlur}
            type="button"
            class="btn-reset flex w-full text-14 leading-[20px] text-gray-800 pt-[20px] pb-[16px] px-[20px] z-1 text-left"
            aria-label="Type de recherche"
        >
            <div class="flex-auto">
                {#if choice != null}
                    <i class={cx(choice.icon, 'text-16 mr-[8px]')} aria-hidden="true"></i>
                    {choice.label}
                {/if}
            </div>
            <i class="tnp tnp-chevron-down" aria-hidden="true" />
        </button>

        {#if $open}
            <div
                class={cx(
                    'z-0 flex max-h-[300px] flex-col',
                    'overflow-y-auto rounded-b-[32px] bg-white p-1',
                    'shadow-mobile-search-header-dropdown focus:!ring-0',
                )}
                use:melt={$menu}
                transition:fly={{ duration: 150, y: -5 }}
            >
                {#each choices as c}
                    {#if choice?.value !== c.value}
                        <div
                            use:melt={$option({ value: c.value, label: c.label })}
                            class={cx(
                                'relative cursor-pointer py-[16px] px-[20px]',
                                'hover:bg-gray-100 focus:z-10',
                                'data-[highlighted]:bg-gray-200',
                                'data-[disabled]:opacity-50',
                            )}
                        >
                            <i class={cx(c.icon, 'text-gray-800 mr-[8px] text-16')} />
                            <span>{c.label}</span>
                        </div>
                    {/if}
                {/each}
            </div>
        {/if}
    </div>
{:else if variant === 'just-icon'}
    <div class="w-full h-full flex items-center justify-center">
        <button
            use:resizeObserver
            use:melt={$trigger}
            on:focus={onFocus}
            on:blur={onBlur}
            type="button"
            class={cx(
                'btn-reset',
                'flex gap-[8px] items-center',
                'text-14 leading-[20px]',
                'bg-gray-100 text-gray-800',
                'px-[8px] py-[10px]',
                'text-left',
                'rounded-[8px]',
                'border border-solid border-gray-300',
            )}
            aria-label="Type de recherche"
        >
            <div class="flex-auto">
                {#if choice != null}
                    <i class={cx(choice.icon, 'text-16')} aria-label={choice.label}></i>
                {/if}
            </div>
            <i class="tnp tnp-chevron-down" aria-hidden={true} />
        </button>
    </div>

    {#if $open}
        {@const optionClass = cx(
            'btn-reset text-left',
            'relative cursor-pointer py-[16px] px-[16px]',
            'focus:z-10',
            'data-[disabled]:opacity-50',
            'bg-gray-100 hover:bg-gray-200 data-[highlighted]:bg-gray-200',
        )}
        {@const iconClass = 'mr-[8px] text-16'}

        <div
            class={cx(
                'right-[41px]',
                'z-0 flex flex-col',
                'bg-gray-100',
                'overflow-y-auto rounded-[8px]',
                'border border-solid border-gray-300',
                'divide-y divide-solid divide-gray-300 *:border-0',
                'shadow-mobile-search-header-dropdown focus:!ring-0',
                'text-13 leading-[120%]',
                'font-bold',
                'text-gray-800',
            )}
            use:melt={$menu}
            transition:fly={{ duration: 150, y: -5 }}
        >
            {#if choice != null}
                <div
                    use:melt={$option({ value: choice.value, label: choice.label })}
                    class={cx(optionClass, 'text-brand-blue')}
                >
                    <i class={cx(choice.icon, iconClass)} />
                    <span>{choice.label}</span>
                </div>
            {/if}
            {#each choices as c}
                {#if choice?.value !== c.value}
                    <div
                        use:melt={$option({ value: c.value, label: c.label })}
                        class={cx(optionClass)}
                    >
                        <i class={cx(c.icon, iconClass)} />
                        <span>{c.label}</span>
                    </div>
                {/if}
            {/each}
        </div>
    {/if}
{:else if variant === 'rounded-icon'}
    <div class="h-full flex py-[13px]">
        <button
            use:resizeObserver
            use:melt={$trigger}
            on:focus={onFocus}
            on:blur={onBlur}
            type="button"
            class={cx(
                'btn-reset',
                'flex gap-[6px] items-center',
                'text-14 leading-[20px]',
                'bg-gray-100 text-gray-800',
                'px-[12px] py-[10px]',
                'text-left',
                'rounded-[25px]',
                'border border-solid border-gray-300',
                'w-[64px]',
            )}
            aria-label="Type de recherche"
        >
            {#if choice != null}
                <i class={cx(choice.icon, 'text-16')} aria-label={choice.label} />
            {/if}
            <i class="tnp tnp-chevron-down" aria-hidden={true} />
        </button>

        {#if $open}
            {@const optionClass = cx(
                'btn-reset text-left',
                'relative cursor-pointer py-[16px] px-[16px]',
                'focus:z-10',
                'data-[disabled]:opacity-50',
                'bg-gray-100 hover:bg-gray-200 data-[highlighted]:bg-gray-200',
            )}
            {@const iconClass = 'mr-[8px] text-16'}

            <div
                class={cx(
                    'z-[101]',
                    'right-[41px]',
                    'flex flex-col',
                    'bg-gray-100',
                    'overflow-y-auto rounded-[20px]',
                    'border border-solid border-gray-300',
                    'divide-y divide-solid divide-gray-300 *:border-0',
                    'shadow-mobile-search-header-dropdown focus:!ring-0',
                    'text-13 leading-[120%]',
                    'font-bold',
                    'text-gray-800',
                    'max-w-[310px]',
                )}
                use:melt={$menu}
                transition:fly={{ duration: 150, y: -5 }}
            >
                {#if choice != null}
                    <button
                        use:melt={$option({ value: choice.value, label: choice.label })}
                        class={cx(optionClass, 'text-brand-blue')}
                    >
                        <i class={cx(choice.icon, iconClass)} />
                        <span>{choice.label}</span>
                    </button>
                {/if}
                {#each choices as c}
                    {#if choice?.value !== c.value}
                        <button
                            use:melt={$option({ value: c.value, label: c.label })}
                            class={cx(optionClass, 's')}
                        >
                            <i class={cx(c.icon, iconClass)} />
                            <span>{c.label}</span>
                        </button>
                    {/if}
                {/each}
            </div>
        {/if}
    </div>
{/if}
